import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ocrPage } from "@/lib/ocrPage";
import { groupRoPages, isRoText } from "@/lib/roPages";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ClaimEditor } from "@/components/hyundai/ClaimEditor";
import { TechRoster, useTechs } from "@/components/hyundai/TechRoster";
import { AddonDialog } from "@/components/hyundai/AddonDialog";
import { AdvisorRoster, StoreSelect, useAdvisors, useStores } from "@/components/hyundai/AdvisorRoster";
import { HYUNDAI } from "@/lib/portalBrands";
import { ArrowLeft, Upload, Loader2, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";

// Text PDFs: one RO per file. Scanned PDFs: read every page, then group pages by RO number (pages /1, /2 … of one RO stay together).
async function roTexts(file, setProgress, failedPages) {
  const { pdfText, openPdf, pageJpeg } = await import("@/lib/pdfLibs");
  const { text, pageTexts } = await pdfText(file);
  if (pageTexts.every(isRoText)) return [text];
  const pdf = await openPdf(file);
  const pages = pageTexts.map((t) => (isRoText(t) ? t : ""));
  const todo = pages.map((t, i) => (t ? 0 : i + 1)).filter(Boolean);
  let next = 0, done = 0;
  const worker = async () => {
    while (next < todo.length) {
      const n = todo[next++];
      try {
        pages[n - 1] = await ocrPage(await pageJpeg(pdf, n));
      } catch (err) {
        pages[n - 1] = "";
        failedPages.push(`${file.name} page ${n}: ${err.message}`);
      }
      setProgress(`Reading scanned pages: ${++done} of ${todo.length} done (${file.name})`);
    }
  };
  await Promise.all(Array.from({ length: Math.min(8, todo.length) }, worker));
  return groupRoPages(pages).map((g) => g.text);
}

const STATUS_STYLE = {
  draft: "bg-slate-500/20 text-slate-200", ready: "bg-emerald-500/20 text-emerald-300", saved: "bg-indigo-500/20 text-indigo-300",
  submitted: "bg-sky-500/20 text-sky-300", returned: "bg-red-500/20 text-red-300",
};

const Summary = ({ claims }) => {
  const clean = claims.filter((c) => c.errors.length === 0).length;
  const issues = claims.reduce((n, c) => n + c.errors.length, 0);
  const cell = (label, value, testid, tone = "") => (
    <div className="rounded-lg border border-border bg-card/60 p-4" data-testid={testid}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className={`mt-1 font-heading text-3xl font-extrabold ${tone}`}>{value}</p>
    </div>
  );
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {cell("Drafts", claims.length, "hy-sum-total")}
      {cell("No return risks", clean, "hy-sum-clean", "text-emerald-300")}
      {cell("Need fixing", claims.length - clean, "hy-sum-flagged", "text-red-300")}
      {cell("Total issues", issues, "hy-sum-issues", "text-amber-300")}
    </div>
  );
};

export default function HyundaiPortal({ cfg = HYUNDAI }) {
  const [claims, setClaims] = useState([]);
  const [techs, loadTechs] = useTechs(cfg.api);
  const { stores, store, setStore } = useStores(`${cfg.key}_store`);
  const [advisors, loadAdvisors] = useAdvisors(!!cfg.advisors);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [open, setOpen] = useState(null);
  const input = useRef(null);
  const techName = (id) => techs.find((t) => t.id === id)?.name;

  const load = useCallback(() => api.get(`${cfg.api}/claims`).then(({ data }) => setClaims(data)).catch(() => toast.error("Could not load drafts")), [cfg.api]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length) return;
    if (cfg.stores && !store) { toast.error("Pick the store these ROs belong to first"); return; }
    setBusy(true);
    const { expandFiles } = await import("@/lib/pdfLibs");
    const files = await expandFiles(list);
    let ok = 0;
    const roSet = new Set();
    const failed = [];
    for (const [i, f] of files.entries()) {
      setProgress(`Reading file ${i + 1} of ${files.length} (${f.name})`);
      let texts = [];
      try {
        texts = await roTexts(f, setProgress, failed);
      } catch (err) {
        failed.push(`${f.name}: ${err.response?.data?.detail || err.message}`);
      }
      let done = 0, next = 0;
      const draft = async () => {
        while (next < texts.length) {
          const j = next++;
          const text = texts[j];
          try {
            const { data } = await api.post(`${cfg.api}/claims/extract`, { text, filename: f.name, ...(cfg.stores && { dealership_id: store }) });
            ok += data.length;
            data.forEach((c) => roSet.add(c[cfg.cols.ro] || c.vin));
          } catch (err) {
            failed.push(`${f.name} RO ${(text.match(/\b(\d{6,8})\/\d{1,2}\b/) || [])[1] || j + 1}: ${err.response?.data?.detail || err.message}`);
          }
          setProgress(`AI drafted ${++done} of ${texts.length} ROs (${f.name})`);
        }
      };
      setProgress(`AI is drafting ${texts.length} RO${texts.length === 1 ? "" : "s"} (${f.name})`);
      await Promise.all(Array.from({ length: Math.min(5, texts.length) }, draft));
    }
    const ros = roSet.size;
    setBusy(false);
    setProgress("");
    toast.success(`Drafted ${ok} claim${ok === 1 ? "" : "s"} from ${ros} RO${ros === 1 ? "" : "s"}${failed.length ? ` - ${failed.length} failed` : ""}`);
    failed.forEach((m) => toast.error(m, { duration: 10000 }));
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete draft ${c[cfg.cols.id]} (RO ${c[cfg.cols.ro] || "?"} line ${c.line || "?"})?`)) return;
    await api.delete(`${cfg.api}/claims/${c.id}`);
    load();
  };
  const saved = (c) => setClaims((xs) => xs.map((x) => (x.id === c.id ? c : x)));
  // Each store sees only its own drafts (plus older drafts saved before stores existed).
  const shown = cfg.stores ? claims.filter((c) => !c.dealership_id || c.dealership_id === store) : claims;

  return (
    <div className="min-h-screen bg-background" data-testid={`${cfg.key}-portal`}>
      <header className="text-white" style={{ background: cfg.header }}>
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-4 px-4 py-5 sm:px-8">
          <Link to="/" className="flex items-center gap-1 text-sm text-white/70 hover:text-white" data-testid="hy-back-link"><ArrowLeft className="h-4 w-4" /> Warranty Compliance Pro</Link>
          <div className="min-w-[260px] flex-1">
            <h1 className="font-heading text-2xl font-extrabold uppercase tracking-tight sm:text-3xl">{cfg.title}</h1>
            <p className="text-sm text-white/70">{cfg.blurb}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <AddonDialog system={cfg.system} />
          {cfg.stores && <StoreSelect stores={stores} store={store} setStore={setStore} />}
          {cfg.advisors && <AdvisorRoster advisors={advisors} onChange={() => { loadAdvisors(); load(); }} store={store} storeName={stores.find((s) => s.id === store)?.name} />}
          <TechRoster techs={techs} onChange={() => { loadTechs(); load(); }} base={cfg.api} store={store} storeName={stores.find((s) => s.id === store)?.name} />
          <input ref={input} type="file" accept=".pdf,.zip" multiple hidden onChange={upload} data-testid="hy-file-input" />
          <Button onClick={() => input.current?.click()} disabled={busy} className="gap-2 text-white hover:opacity-90" style={{ background: cfg.accent }} data-testid="hy-upload-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {busy ? "Drafting..." : "Upload ROs"}
          </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1400px] space-y-6 px-4 py-6 sm:px-8">
        {progress && <p className="rounded-md border border-sky-500/30 bg-sky-500/10 p-3 text-sm text-sky-200" data-testid="hy-progress">{progress}</p>}
        <Summary claims={shown} />
        <div className="overflow-x-auto rounded-lg border border-border bg-card/40">
          <Table data-testid="hy-claims-table">
            <TableHeader>
              <TableRow>
                <TableHead>{cfg.cols.idLabel}</TableHead><TableHead>{cfg.cols.roLabel}</TableHead><TableHead>Line</TableHead><TableHead>VIN</TableHead><TableHead>Type</TableHead>
                <TableHead>Op Code</TableHead><TableHead>Technician</TableHead><TableHead>Status</TableHead><TableHead>Errors</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.length === 0 && (
                <TableRow><TableCell colSpan={10} className="py-10 text-center text-muted-foreground" data-testid="hy-empty">No drafts yet - upload {cfg.make} repair orders to start.</TableCell></TableRow>
              )}
              {shown.map((c) => (
                <TableRow key={c.id} className="cursor-pointer align-top" onClick={() => setOpen(c)} data-testid={`hy-claim-row-${c.id}`}>
                  <TableCell className="font-mono font-semibold">{c[cfg.cols.id]}</TableCell>
                  <TableCell className="font-mono">{c[cfg.cols.ro] || "-"}<div className="text-[10px] text-muted-foreground">{cfg.key === "hyundai" && c.ro_full}{cfg.stores && !c.dealership_id && <span className="text-amber-400" data-testid={`hy-claim-nostore-${c.id}`}> no store</span>}</div></TableCell>
                  <TableCell className="font-mono">{c.line ? `#${c.line}` : "-"}</TableCell>
                  <TableCell className="font-mono text-xs">{c.vin || "-"}</TableCell>
                  <TableCell>{c[cfg.typeKey]}{c.campaign && <div className="font-mono text-[10px] text-muted-foreground">{c.campaign}</div>}</TableCell>
                  <TableCell className="font-mono">{c[cfg.cols.op] || "-"}</TableCell>
                  <TableCell>{techName(c.technician_id) || (c.tech_number && `Tech # ${c.tech_number}`) || (c.technician_id && !techs.length ? "…" : <span className="text-amber-300">Not picked</span>)}</TableCell>
                  <TableCell><span className={`rounded px-2 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLE[c.status] || ""}`}>{c.status}</span></TableCell>
                  <TableCell className="max-w-[360px]" data-testid={`hy-claim-errors-${c.id}`}>
                    {c.errors.length === 0
                      ? <span className="flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-4 w-4" /> None</span>
                      : <ul className="space-y-0.5 text-xs text-red-200">{c.errors.map((e, i) => <li key={i} className="flex gap-1"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{e}</li>)}</ul>}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`hy-claim-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
      <ClaimEditor claim={open} techs={techs} advisors={advisors} cfg={cfg} onClose={() => setOpen(null)} onSaved={saved} />
    </div>
  );
}
